import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import {
  adminGetTours, adminCreateTour, adminUpdateTour, adminDeleteTour, adminDuplicateTour,
} from "./adminApi";
import { Text, Area, Select, Toggle, ImageField, ListEditor, ItineraryEditor, FaqEditor, GalleryEditor, Modal, Label } from "./ui";

const CATEGORIES = [
  "Golden Triangle", "Rajasthan Tours", "Kerala Tours", "Kashmir Tours", "Ladakh Tours",
  "South India Tours", "Himalayan Tours", "Wildlife Tours", "Spiritual Tours", "Buddhist Tours",
  "Luxury Tours", "Honeymoon Tours", "Family Tours", "Adventure Tours", "Yoga & Wellness Tours",
  "Festival Tours", "Customized Tours",
];

const blank = {
  name: "", category: "Customized Tours", duration_label: "", duration_days: 0,
  banner_image: "", gallery: [], overview: "", highlights: [], itinerary: [],
  inclusions: [], exclusions: [], best_time: "", map_embed: "", faqs: [],
  destinations: [], featured: false, status: "published",
  seo: { meta_title: "", meta_description: "", keywords: "" },
};

export default function AdminTours() {
  const [tours, setTours] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminGetTours().then(setTours).catch(() => toast.error("Failed to load tours"));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...blank });
  const openEdit = (t) => setEditing({ ...blank, ...t, seo: { ...blank.seo, ...(t.seo || {}) } });

  const save = async () => {
    if (!editing.name) { toast.error("Tour name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...editing, duration_days: Number(editing.duration_days) || 0 };
      if (editing.id) await adminUpdateTour(editing.id, payload);
      else await adminCreateTour(payload);
      toast.success("Tour saved");
      setEditing(null); load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await adminDeleteTour(t.id); toast.success("Deleted"); load(); };
  const dup = async (t) => { await adminDuplicateTour(t.id); toast.success("Duplicated as draft"); load(); };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setSeo = (k, v) => setEditing((e) => ({ ...e, seo: { ...e.seo, [k]: v } }));

  const filtered = tours.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()) || t.category.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <Header title="Tours" count={tours.length} onNew={openNew} q={q} setQ={setQ} newLabel="Add Tour" />

      <div className="bg-white border border-[#d4af37]/25" data-testid="tours-table">
        {filtered.map((t) => (
          <div key={t.id} className="flex items-center gap-4 px-5 py-3 border-b border-[#d4af37]/15 last:border-0">
            <img src={t.banner_image} alt="" className="w-14 h-14 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#3b1a1a] truncate">{t.name}</div>
              <div className="text-xs text-[#5a4b4b]">{t.category} · {t.duration_label || "Flexible"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {t.featured && <span className="text-[10px] tracking-[0.1em] uppercase bg-[#d4af37]/20 text-[#7c1d1d] px-2 py-1">Featured</span>}
              <span className={`text-[10px] tracking-[0.1em] uppercase px-2 py-1 ${t.status === "published" ? "bg-[#14523d]/15 text-[#14523d]" : "bg-[#5a4b4b]/15 text-[#5a4b4b]"}`}>{t.status}</span>
              <button onClick={() => dup(t)} title="Duplicate" className="w-8 h-8 grid place-items-center text-[#5a4b4b] hover:text-[#7c1d1d]"><Copy className="w-4 h-4" /></button>
              <button onClick={() => openEdit(t)} data-testid={`edit-tour-${t.id}`} title="Edit" className="w-8 h-8 grid place-items-center text-[#14523d] hover:bg-[#14523d] hover:text-white"><Edit className="w-4 h-4" /></button>
              <button onClick={() => del(t)} title="Delete" className="w-8 h-8 grid place-items-center text-[#7c1d1d] hover:bg-[#7c1d1d] hover:text-white"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="px-5 py-10 text-center text-[#5a4b4b]">No tours.</div>}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? "Edit Tour" : "Add Tour"} wide>
        {editing && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Text label="Tour Name *" value={editing.name} onChange={(v) => set("name", v)} placeholder="e.g. 5 Days Golden Triangle" />
              <Select label="Category" value={editing.category} onChange={(v) => set("category", v)} options={CATEGORIES} />
              <Text label="Duration Label" value={editing.duration_label} onChange={(v) => set("duration_label", v)} placeholder="e.g. 5 Days · 4 Nights" />
              <Text label="Duration (days)" type="number" value={editing.duration_days} onChange={(v) => set("duration_days", v)} />
              <Text label="Best Time" value={editing.best_time} onChange={(v) => set("best_time", v)} placeholder="e.g. October to March" />
              <div className="flex items-end gap-6">
                <Toggle label="Featured" value={editing.featured} onChange={(v) => set("featured", v)} />
                <Select label="Status" value={editing.status} onChange={(v) => set("status", v)} options={["published", "draft"]} />
              </div>
            </div>
            <ImageField label="Banner Image" value={editing.banner_image} onChange={(v) => set("banner_image", v)} folder="tours" />
            <Area label="Overview" value={editing.overview} onChange={(v) => set("overview", v)} rows={3} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ListEditor label="Destinations Covered" items={editing.destinations} onChange={(v) => set("destinations", v)} placeholder="e.g. Jaipur" />
              <ListEditor label="Highlights" items={editing.highlights} onChange={(v) => set("highlights", v)} placeholder="Add highlight" />
            </div>
            <ItineraryEditor items={editing.itinerary} onChange={(v) => set("itinerary", v)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ListEditor label="Inclusions" items={editing.inclusions} onChange={(v) => set("inclusions", v)} />
              <ListEditor label="Exclusions" items={editing.exclusions} onChange={(v) => set("exclusions", v)} />
            </div>
            <GalleryEditor items={editing.gallery} onChange={(v) => set("gallery", v)} folder="tours" />
            <FaqEditor items={editing.faqs} onChange={(v) => set("faqs", v)} />
            <Text label="Map Embed URL (optional)" value={editing.map_embed} onChange={(v) => set("map_embed", v)} placeholder="Google Maps embed src" />
            <div className="border-t border-[#d4af37]/25 pt-4">
              <Label>SEO</Label>
              <div className="grid grid-cols-1 gap-3 mt-1">
                <Text label="Meta Title" value={editing.seo.meta_title} onChange={(v) => setSeo("meta_title", v)} />
                <Area label="Meta Description" value={editing.seo.meta_description} onChange={(v) => setSeo("meta_description", v)} rows={2} />
                <Text label="Keywords" value={editing.seo.keywords} onChange={(v) => setSeo("keywords", v)} />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={save} disabled={saving} data-testid="save-tour" className="bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60">{saving ? "Saving…" : "Save Tour"}</button>
              <button onClick={() => setEditing(null)} className="border border-[#d4af37]/40 text-[#3b1a1a] px-8 py-3 text-xs tracking-[0.2em] uppercase">Cancel</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function Header({ title, count, onNew, q, setQ, newLabel }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Manage</div>
        <h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">{title} <span className="text-[#d4af37] text-2xl">({count})</span></h1>
      </div>
      <div className="flex items-center gap-3">
        {setQ && (
          <div className="flex items-center gap-2 border border-[#d4af37]/40 bg-white px-3 py-2">
            <Search className="w-4 h-4 text-[#7c1d1d]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-transparent outline-none text-sm w-40" />
          </div>
        )}
        {onNew && <button onClick={onNew} data-testid="add-new-btn" className="inline-flex items-center gap-2 bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase"><Plus className="w-4 h-4" /> {newLabel}</button>}
      </div>
    </div>
  );
}
